import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, WS_URL, getToken, errMsg, CLIENT_KIND } from "./api";
import { useAuth } from "./auth";
import { analytics } from "./analytics";
import { HuddleClient } from "./huddle";
import { mergePrefs } from "./prefs";
import { buildNotification, notifyReason, shouldSuppress, showDesktopNotification } from "./notify";
import { configureSounds, playNotification, playSound } from "./sounds";
import { flushOutbox, isNetworkError, outbox } from "./outbox";
import { fetchMessage, primeMessage } from "@/components/MessageUnfurl";

const ChatContext = createContext(null);

const upsertList = (list, m) => {
  const idx = list.findIndex((x) => x.id === m.id || (m.client_id && x.client_id && x.client_id === m.client_id));
  if (idx === -1) return [...list, m];
  const next = [...list];
  next[idx] = { ...next[idx], ...m, is_saved: m.is_saved ?? next[idx].is_saved };
  return next;
};

export function ChatProvider({ children }) {
  const { user, setUser, refresh } = useAuth();
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [msgState, setMsgState] = useState({});
  const [threads, setThreads] = useState({});
  const [huddles, setHuddles] = useState([]);
  const [typing, setTyping] = useState({});
  const [panel, setPanel] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [outboxCount, setOutboxCount] = useState(0);
  const everConnected = useRef(false);
  const reconnectNow = useRef(() => {});
  const [myHuddleId, setMyHuddleId] = useState(null);
  const [huddleState, setHuddleState] = useState({ streams: {}, speaking: {}, muted: false, micError: null, connected: 0 });
  const [modal, setModal] = useState(null);
  const [newSince, setNewSince] = useState({});
  const [jump, setJump] = useState(null);
  const wsRef = useRef(null);
  const currentRef = useRef(null);
  const conversationsRef = useRef([]);
  const msgStateRef = useRef({});
  const threadsRef = useRef({});
  const panelRef = useRef(null);
  const usersRef = useRef([]);
  const prefsRef = useRef(mergePrefs(null));
  const myHuddleRef = useRef(null);
  const huddlesRef = useRef([]);
  huddlesRef.current = huddles;
  const huddleClient = useRef(null);
  const handlerRef = useRef(() => {});
  const readTimer = useRef(null);
  const typingTimers = useRef({});
  const lastTyping = useRef({});
  const meId = user?.id;
  const prefs = useMemo(() => mergePrefs(user?.prefs), [user?.prefs]);

  currentRef.current = currentId;
  conversationsRef.current = conversations;
  msgStateRef.current = msgState;
  threadsRef.current = threads;
  panelRef.current = panel;
  usersRef.current = users;
  prefsRef.current = prefs;
  myHuddleRef.current = myHuddleId;

  const usersByHandle = useMemo(() => Object.fromEntries(users.map((u) => [u.handle?.toLowerCase(), u])), [users]);
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const current = useMemo(() => conversations.find((c) => c.id === currentId) || null, [conversations, currentId]);

  const loadConversations = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConversations(data);
    return data;
  }, []);
  const loadUsers = useCallback(async () => setUsers((await api.get("/users")).data), []);
  const loadHuddles = useCallback(async () => setHuddles((await api.get("/huddles/active")).data), []);

  useEffect(() => {
    if (!meId) return;
    loadConversations().catch(() => {});
    loadUsers().catch(() => {});
    loadHuddles().catch(() => {});
  }, [meId, loadConversations, loadUsers, loadHuddles]);

  const markRead = useCallback((cid) => {
    clearTimeout(readTimer.current);
    readTimer.current = setTimeout(() => api.post(`/conversations/${cid}/read`).catch(() => {}), 400);
    const ts = new Date().toISOString();
    setConversations((cs) => cs.map((c) => (c.id === cid ? { ...c, unread_count: 0, mention_count: 0, last_read_at: ts } : c)));
  }, []);

  const upsertMessage = useCallback((m) => {
    primeMessage(m);
    if (m.parent_id) {
      setThreads((t) => (t[m.parent_id] ? { ...t, [m.parent_id]: { ...t[m.parent_id], replies: upsertList(t[m.parent_id].replies, m) } } : t));
    } else {
      setMsgState((s) => {
        const st = s[m.conversation_id];
        if (!st) return s;
        if (st.has_more_after && !st.messages.some((x) => x.id === m.id || (m.client_id && x.client_id === m.client_id))) return s;
        return { ...s, [m.conversation_id]: { ...st, messages: upsertList(st.messages, m) } };
      });
      setThreads((t) => (t[m.id] ? { ...t, [m.id]: { ...t[m.id], parent: { ...t[m.id].parent, ...m } } } : t));
    }
  }, []);

  const mentionsMe = useCallback((m) => m.mention_channel || m.mention_here || (m.mentions || []).includes(meId), [meId]);

  const maybeNotify = (m) => {
    if (m.user_id === meId) return;
    const p = prefsRef.current;
    const conv = conversationsRef.current.find((c) => c.id === m.conversation_id);
    const parent = m.parent_id ? (msgStateRef.current[m.conversation_id]?.messages || []).find((x) => x.id === m.parent_id) || threadsRef.current[m.parent_id]?.parent : null;
    const reason = notifyReason(m, { meId, prefs: p, conversation: conv, parent, mentionsMe: mentionsMe(m) });
    if (!reason) return;
    const focused = document.visibilityState === "visible" && document.hasFocus();
    const viewing = focused && m.conversation_id === currentRef.current && (!m.parent_id || (panelRef.current?.type === "thread" && panelRef.current.id === m.parent_id));
    if (viewing || shouldSuppress(p)) return;
    const sender = usersRef.current.find((u) => u.id === m.user_id);
    const n = buildNotification(m, conv, sender);
    let shown = false;
    if (p.desktop) {
      shown = !!showDesktopNotification({ ...n, tag: `tc-${m.conversation_id}`, onClick: () => {
        analytics.track("notification_clicked", { category: "notifications", target: "message", target_id: m.id, conversation_id: m.conversation_id, label: reason });
        navigate(`/client/${m.conversation_id}?m=${m.id}`);
      } });
    }
    if (p.sound) playNotification();
    analytics.track("notification_shown", { category: "notifications", target: "message", target_id: m.id, conversation_id: m.conversation_id, label: reason, metadata: { desktop: shown, sound: p.sound, focused } });
  };

  handlerRef.current = (ev) => {
    switch (ev.type) {
      case "message.new": {
        const m = ev.message;
        upsertMessage(m);
        maybeNotify(m);
        const isCurrent = m.conversation_id === currentRef.current && document.visibilityState === "visible";
        if (!m.parent_id) {
          setConversations((cs) => cs.map((c) => {
            if (c.id !== m.conversation_id) return c;
            if (m.user_id === meId || isCurrent) return { ...c, last_message_at: m.created_at };
            return { ...c, last_message_at: m.created_at, unread_count: (c.unread_count || 0) + 1, mention_count: (c.mention_count || 0) + (mentionsMe(m) ? 1 : 0) };
          }));
          if (isCurrent && m.user_id !== meId) markRead(m.conversation_id);
        } else if (m.user_id !== meId && mentionsMe(m)) {
          if (isCurrent) markRead(m.conversation_id);
          else setConversations((cs) => cs.map((c) => (c.id === m.conversation_id ? { ...c, mention_count: (c.mention_count || 0) + 1 } : c)));
        }
        if (m.user_id !== meId) {
          const key = m.parent_id || m.conversation_id;
          setTyping((t) => (t[key] ? { ...t, [key]: Object.fromEntries(Object.entries(t[key]).filter(([uid]) => uid !== m.user_id)) } : t));
        }
        break;
      }
      case "message.updated": upsertMessage(ev.message); break;
      case "message.deleted":
        setMsgState((s) => (s[ev.conversation_id] ? { ...s, [ev.conversation_id]: { ...s[ev.conversation_id], messages: s[ev.conversation_id].messages.filter((x) => x.id !== ev.id) } } : s));
        if (ev.parent_id) setThreads((t) => (t[ev.parent_id] ? { ...t, [ev.parent_id]: { ...t[ev.parent_id], replies: t[ev.parent_id].replies.filter((x) => x.id !== ev.id) } } : t));
        else setPanel((p) => (p?.type === "thread" && p.id === ev.id ? null : p));
        break;
      case "conversation.new":
        setConversations((cs) => (cs.some((c) => c.id === ev.conversation.id) ? cs : [...cs, { unread_count: 0, mention_count: 0, is_starred: false, last_read_at: null, ...ev.conversation }]));
        break;
      case "conversation.updated":
        if (ev.conversation.archived) {
          setConversations((cs) => cs.filter((c) => c.id !== ev.conversation.id));
          if (currentRef.current === ev.conversation.id) { if (ev.actor_id !== meId) toast.info(`#${ev.conversation.name} was archived`); navigate("/client"); }
          break;
        }
        setConversations((cs) => cs.map((c) => (c.id === ev.conversation.id ? { ...c, ...ev.conversation, unread_count: c.unread_count, mention_count: c.mention_count, is_starred: c.is_starred, last_read_at: c.last_read_at } : c)));
        break;
      case "conversation.left":
        setConversations((cs) => cs.filter((c) => c.id !== ev.conversation_id));
        if (currentRef.current === ev.conversation_id) navigate("/client");
        break;
      case "presence":
        setUsers((us) => us.map((u) => (u.id === ev.user_id ? { ...u, presence: ev.presence } : u)));
        setConversations((cs) => cs.map((c) => ({ ...c, members_info: (c.members_info || []).map((u) => (u.id === ev.user_id ? { ...u, presence: ev.presence } : u)) })));
        if (ev.user_id === meId) setUser((u) => (u ? { ...u, presence: ev.presence } : u));
        break;
      case "user.new":
      case "user.updated":
        setUsers((us) => (us.some((u) => u.id === ev.user.id) ? us.map((u) => (u.id === ev.user.id ? { ...u, ...ev.user } : u)) : [...us, ev.user]));
        if (ev.user.id === meId) {
          if (ev.user.is_active === false || ev.user.mobile_access !== userRef.current?.mobile_access) { refresh(); break; }
          setUser((u) => (u ? { ...u, ...ev.user } : u));
        }
        break;
      case "mobile.updated":
        refresh();
        break;
      case "prefs.updated":
        setUser((u) => (u ? { ...u, prefs: ev.prefs } : u));
        break;
      case "permissions.updated":
        setUser((u) => (u ? { ...u, role: ev.role, permissions: ev.permissions } : u));
        toast.info("Your permissions were updated by an admin");
        break;
      case "tracking.updated":
        analytics.setTracking(ev.tracking);
        break;
      case "health.updated":
        window.dispatchEvent(new CustomEvent("tc:health", { detail: ev.status }));
        break;
      case "huddle.updated": {
        const h = ev.huddle;
        const prev = huddlesRef.current.find((x) => x.id === h.id);
        setHuddles((hs) => (h.active ? (hs.some((x) => x.id === h.id) ? hs.map((x) => (x.id === h.id ? h : x)) : [...hs, h]) : hs.filter((x) => x.id !== h.id)));
        if (myHuddleRef.current === h.id) {
          if (!h.active || !h.participants.includes(meId)) {
            huddleClient.current?.leave();
            huddleClient.current = null;
            setMyHuddleId(null);
            playSound("huddle_end");
          } else {
            huddleClient.current?.syncParticipants(h.participants);
            const before = new Set((prev?.participants || []).filter((p) => p !== meId));
            const after = new Set(h.participants.filter((p) => p !== meId));
            if ([...after].some((p) => !before.has(p))) playSound("participant_join");
            else if ([...before].some((p) => !after.has(p))) playSound("participant_leave");
          }
        } else if (h.active && !prev && h.conversation_id === currentRef.current && h.started_by !== meId) playSound("huddle_start");
        break;
      }
      case "huddle.signal": huddleClient.current?.handleSignal(ev.from, ev.signal); break;
      case "typing": {
        const key = ev.parent_id || ev.conversation_id;
        setTyping((t) => ({ ...t, [key]: { ...(t[key] || {}), [ev.user_id]: ev.display_name } }));
        clearTimeout(typingTimers.current[`${key}:${ev.user_id}`]);
        typingTimers.current[`${key}:${ev.user_id}`] = setTimeout(() => {
          setTyping((t) => {
            if (!t[key]) return t;
            const next = { ...t[key] };
            delete next[ev.user_id];
            return { ...t, [key]: next };
          });
        }, 4000);
        break;
      }
      default: break;
    }
  };

  useEffect(() => {
    if (!meId) return undefined;
    let closed = false;
    let retry = 1000;
    let ws;
    let timer = null;
    const connect = () => {
      timer = null;
      if (closed || ws?.readyState === 0 || ws?.readyState === 1) return;
      ws = new WebSocket(`${WS_URL}?token=${getToken()}&client=${CLIENT_KIND}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        retry = 1000;
        loadHuddles().catch(() => {});
        if (everConnected.current) resyncRef.current(); // came back after a drop: pull anything we missed
        everConnected.current = true;
      };
      ws.onmessage = (e) => { try { handlerRef.current(JSON.parse(e.data)); } catch { /* ignore */ } };
      ws.onclose = (e) => {
        setConnected(false);
        if (e?.code === 4401 || e?.code === 4403) { api.get("/auth/me").catch(() => {}); return; }
        if (!closed) timer = setTimeout(connect, (retry = Math.min(retry * 2, 15000)));
      };
      ws.onerror = () => ws.close();
    };
    reconnectNow.current = () => { if (closed) return; clearTimeout(timer); retry = 1000; if (ws && ws.readyState !== 1 && ws.readyState !== 0) connect(); else if (!ws) connect(); };
    const onOnline = () => reconnectNow.current();
    const onVisible = () => { if (document.visibilityState === "visible" && ws?.readyState !== 1) reconnectNow.current(); };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    connect();
    const ping = setInterval(() => ws?.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })), 25000);
    return () => { closed = true; clearInterval(ping); clearTimeout(timer); window.removeEventListener("online", onOnline); document.removeEventListener("visibilitychange", onVisible); ws?.close(); };
  }, [meId, loadHuddles]);

  // ---- resilience: resync after reconnect, offline outbox ----
  const resyncRef = useRef(() => {});
  resyncRef.current = async () => {
    analytics.track("ws_resync", { category: "reliability" });
    try {
      await Promise.all([loadConversations(), api.get("/auth/me").then((r) => setUser((u) => (u ? { ...u, ...r.data.user } : u)))]);
      const cid = currentRef.current;
      if (cid) await loadMessages(cid);
      if (panelRef.current?.type === "thread") await loadThread(panelRef.current.id);
    } catch { /* will retry on the next reconnect */ }
    flushQueued();
  };
  const flushQueued = useCallback(() => flushOutbox({
    userId: meId,
    onSent: (data) => { upsertMessage({ ...data, pending: false }); },
    onFailed: (e, item) => {
      setMsgState((s) => (s[item.conversation_id] ? { ...s, [item.conversation_id]: { ...s[item.conversation_id], messages: s[item.conversation_id].messages.filter((x) => x.client_id !== item.client_id) } } : s));
      toast.error(`A queued message couldn't be sent: ${errMsg(e)}`);
    },
  }).then((n) => { if (n) toast.success(n === 1 ? "Your queued message was sent" : `${n} queued messages were sent`); }), [meId, upsertMessage]);
  useEffect(() => outbox.subscribe((items) => setOutboxCount(items.filter((x) => x.user_id === meId).length)), [meId]);
  useEffect(() => {
    if (!meId) return undefined;
    const iv = setInterval(() => { if (outbox.forUser(meId).length && navigator.onLine !== false) flushQueued(); }, 8000);
    const onOnline = () => flushQueued();
    window.addEventListener("online", onOnline);
    return () => { clearInterval(iv); window.removeEventListener("online", onOnline); };
  }, [meId, flushQueued]);
  useEffect(() => { configureSounds(prefs); }, [prefs]);

  const wsSend = useCallback((payload) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(payload));
  }, []);

  const loadMessages = useCallback(async (cid, opts) => {
    const o = typeof opts === "string" ? { before: opts } : opts || {};
    const { data } = await api.get(`/conversations/${cid}/messages`, { params: o });
    data.messages.forEach(primeMessage);
    setMsgState((s) => {
      const st = s[cid];
      const prev = st?.messages || [];
      let merged;
      if (o.around || (!o.before && !o.after && st?.has_more_after)) merged = data.messages;
      else if (o.before) merged = [...data.messages, ...prev];
      else if (o.after) merged = data.messages.reduce((acc, m) => upsertList(acc, m), prev);
      else merged = data.messages.reduce((acc, m) => upsertList(acc, m), prev.filter((p) => !data.messages.some((d) => d.id === p.id)));
      merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const has_more = o.after ? (st?.has_more ?? data.has_more) : data.has_more;
      const has_more_after = o.before ? (st?.has_more_after ?? false) : !!data.has_more_after;
      return { ...s, [cid]: { messages: merged, has_more, has_more_after, loaded: true } };
    });
    if (!o.before && !o.after) setNewSince((ns) => (ns[cid] !== undefined ? ns : { ...ns, [cid]: data.last_read_at || null }));
    setConversations((cs) => (cs.some((c) => c.id === cid) ? cs : [...cs, { unread_count: 0, mention_count: 0, last_read_at: data.last_read_at, ...data.conversation }]));
    return data;
  }, []);

  const clearNewSince = useCallback((cid) => setNewSince((ns) => ({ ...ns, [cid]: null })), []);
  const clearJump = useCallback(() => {
    setJump(null);
    if (/[?&]m=/.test(window.location.search)) navigate(window.location.pathname, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const onFocus = () => currentRef.current && markRead(currentRef.current);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [markRead]);

  const totalMentions = useMemo(() => conversations.reduce((a, c) => a + (c.mention_count || 0), 0), [conversations]);
  const dmUnread = useMemo(() => conversations.filter((c) => c.type !== "channel").reduce((a, c) => a + (c.unread_count || 0), 0), [conversations]);
  useEffect(() => {
    const badge = prefs.title_badge === false ? 0 : totalMentions + dmUnread;
    const where = current ? `${current.type === "channel" ? "#" : ""}${current.display_name} - ` : "";
    document.title = `${badge ? `(${badge}) ` : ""}${where}Total Chat`;
  }, [totalMentions, dmUnread, current, prefs.title_badge]);

  const sendMessage = useCallback(async (cid, text, parentId, fileIds = []) => {
    const client_id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      const { data } = await api.post(`/conversations/${cid}/messages`, { text, parent_id: parentId || null, client_id, file_ids: fileIds });
      upsertMessage(data);
      setConversations((cs) => cs.map((c) => (c.id === cid ? { ...c, last_message_at: data.created_at, draft: parentId ? c.draft : null } : c)));
      playSound("message_sent");
      return data;
    } catch (e) {
      if (!isNetworkError(e) || fileIds.length) throw e; // permission/validation errors surface normally; attachments aren't queued
      outbox.add({ client_id, user_id: meId, conversation_id: cid, text, parent_id: parentId || null, file_ids: [] });
      const me = usersRef.current.find((u) => u.id === meId);
      const pending = { id: `pending-${client_id}`, client_id, conversation_id: cid, user_id: meId, user: me, text, parent_id: parentId || null, created_at: new Date().toISOString(), reactions: [], files: [], pending: true };
      upsertMessage(pending);
      analytics.track("outbox_queued", { category: "reliability", conversation_id: cid });
      toast.info("You're offline — the message is queued and will send automatically", { id: "outbox" });
      return pending;
    }
  }, [upsertMessage, meId]);

  const editMessage = useCallback(async (id, text) => upsertMessage((await api.patch(`/messages/${id}`, { text })).data), [upsertMessage]);
  const deleteMessage = useCallback(async (id) => { await api.delete(`/messages/${id}`); }, []);
  const toggleReaction = useCallback(async (id, name) => {
    try { upsertMessage((await api.post(`/messages/${id}/reactions`, { name })).data); } catch (e) { toast.error(errMsg(e)); }
  }, [upsertMessage]);
  const can = useCallback((key) => !!user?.is_admin || !!user?.permissions?.[key], [user]);
  const togglePin = useCallback(async (id) => upsertMessage((await api.post(`/messages/${id}/pin`)).data), [upsertMessage]);
  const toggleSave = useCallback(async (m) => {
    const { data } = await api.post(`/messages/${m.id}/save`);
    upsertMessage({ ...m, is_saved: data.is_saved });
    toast.success(data.is_saved ? "Saved for later" : "Removed from Later");
    return data.is_saved;
  }, [upsertMessage]);
  const forwardMessage = useCallback(async (id, cid, comment) => (await api.post(`/messages/${id}/forward`, { conversation_id: cid, comment })).data, []);

  const loadThread = useCallback(async (pid) => {
    const { data } = await api.get(`/messages/${pid}/replies`);
    setThreads((t) => ({ ...t, [pid]: data }));
    return data;
  }, []);
  const openThread = useCallback((pid, cid) => {
    setPanel({ type: "thread", id: pid });
    analytics.track("thread_opened", { category: "navigation", target: "message", target_id: pid, conversation_id: cid });
    loadThread(pid).catch((e) => toast.error(errMsg(e)));
  }, [loadThread]);
  const openProfile = useCallback((uid) => {
    setPanel({ type: "profile", userId: uid });
    analytics.track("profile_opened", { category: "navigation", target: "user", target_id: uid });
  }, []);
  const closePanel = useCallback(() => setPanel(null), []);

  const jumpToMessage = useCallback(async (cid, mid) => {
    setJump({ cid, mid, parentId: null, pending: true });
    try {
      const [m] = await Promise.all([fetchMessage(mid), loadMessages(cid, { around: mid })]);
      if (m.parent_id) openThread(m.parent_id, cid);
      setJump({ cid, mid, parentId: m.parent_id || null, pending: false });
      analytics.track("message_link_followed", { category: "navigation", target: "message", target_id: mid, conversation_id: cid, metadata: { in_thread: !!m.parent_id } });
    } catch (e) {
      setJump(null);
      toast.error(e?.response?.status === 404 ? "That message isn't available" : errMsg(e));
      loadMessages(cid).catch(() => {});
    }
  }, [loadMessages, openThread]);

  const selectConversation = useCallback((cid, mid) => {
    const prev = currentRef.current;
    setCurrentId(cid);
    if (prev && prev !== cid) setNewSince((ns) => { const n = { ...ns }; delete n[prev]; return n; });
    if (!cid) return;
    const c = conversationsRef.current.find((x) => x.id === cid);
    if (c) setNewSince((ns) => (ns[cid] !== undefined ? ns : { ...ns, [cid]: c.last_read_at || null }));
    analytics.setContext({ conversation_id: cid });
    analytics.track("conversation_viewed", { category: "navigation", target: "conversation", target_id: cid, conversation_id: cid, metadata: mid ? { via_link: mid } : undefined });
    if (mid) jumpToMessage(cid, mid);
    else loadMessages(cid).catch((e) => { toast.error(errMsg(e)); navigate("/client"); });
    markRead(cid);
  }, [loadMessages, markRead, navigate, jumpToMessage]);

  const openConversation = useCallback((cid) => navigate(`/client/${cid}`), [navigate]);
  const toggleStar = useCallback(async (cid) => {
    const { data } = await api.post(`/conversations/${cid}/star`);
    setConversations((cs) => cs.map((c) => (c.id === cid ? { ...c, is_starred: data.is_starred } : c)));
  }, []);
  const createChannel = useCallback(async (body) => {
    const { data } = await api.post("/conversations", { type: "channel", ...body });
    setConversations((cs) => (cs.some((c) => c.id === data.id) ? cs : [...cs, data]));
    navigate(`/client/${data.id}`);
    return data;
  }, [navigate]);
  const openDm = useCallback(async (memberIds, { dms = false } = {}) => {
    const { data } = await api.post("/conversations", { type: "dm", member_ids: memberIds });
    setConversations((cs) => (cs.some((c) => c.id === data.id) ? cs : [...cs, data]));
    analytics.track("dm_opened", { category: "navigation", target: "conversation", target_id: data.id, conversation_id: data.id });
    navigate(dms ? `/client/dms/${data.id}` : `/client/${data.id}`);
    return data;
  }, [navigate]);
  const joinChannel = useCallback(async (cid) => {
    const { data } = await api.post(`/conversations/${cid}/join`);
    setConversations((cs) => (cs.some((c) => c.id === cid) ? cs.map((c) => (c.id === cid ? { ...c, ...data } : c)) : [...cs, { unread_count: 0, mention_count: 0, ...data }]));
    return data;
  }, []);
  const leaveChannel = useCallback(async (cid) => {
    await api.post(`/conversations/${cid}/leave`);
    setConversations((cs) => cs.filter((c) => c.id !== cid));
    setPanel(null);
    navigate("/client");
  }, [navigate]);
  const updateConversation = useCallback(async (cid, body) => {
    const { data } = await api.patch(`/conversations/${cid}`, body);
    setConversations((cs) => cs.map((c) => (c.id === cid ? { ...c, ...data, unread_count: c.unread_count, mention_count: c.mention_count, is_starred: c.is_starred } : c)));
    return data;
  }, []);
  const addMembers = useCallback(async (cid, ids) => {
    const { data } = await api.post(`/conversations/${cid}/members`, { user_ids: ids });
    setConversations((cs) => cs.map((c) => (c.id === cid ? { ...c, ...data, unread_count: c.unread_count, mention_count: c.mention_count, is_starred: c.is_starred } : c)));
    return data;
  }, []);
  const saveDraft = useCallback((cid, text, parentId) => {
    api.put(`/conversations/${cid}/draft`, { text, parent_id: parentId || null }).catch(() => {});
    if (!parentId) setConversations((cs) => cs.map((c) => (c.id === cid ? { ...c, draft: text.trim() ? text : null } : c)));
  }, []);
  const sendTyping = useCallback((cid, parentId) => {
    const key = `${cid}:${parentId || ""}`;
    const nowTs = Date.now();
    if (nowTs - (lastTyping.current[key] || 0) < 2500) return;
    lastTyping.current[key] = nowTs;
    wsSend({ type: "typing", conversation_id: cid, parent_id: parentId || null });
  }, [wsSend]);

  const joinHuddle = useCallback(async (cid) => {
    try {
      if (myHuddleRef.current) {
        huddleClient.current?.leave();
        huddleClient.current = null;
      }
      const { data: h } = await api.post(`/conversations/${cid}/huddle/join`);
      const client = new HuddleClient({
        sendSignal: (to, signal) => wsSend({ type: "huddle.signal", to, huddle_id: h.id, signal }),
        onChange: (st) => setHuddleState(st),
      });
      huddleClient.current = client;
      setMyHuddleId(h.id);
      setHuddles((hs) => (hs.some((x) => x.id === h.id) ? hs.map((x) => (x.id === h.id ? h : x)) : [...hs, h]));
      playSound((h.participants || []).length <= 1 ? "huddle_start" : "huddle_join");
      await client.join(h, meId);
      if (client.micError) toast.warning(`Joined without microphone: ${client.micError}`);
      analytics.track("huddle_ui_joined", { category: "huddles", target: "huddle", target_id: h.id, conversation_id: cid });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [meId, wsSend]);
  const leaveHuddle = useCallback(async () => {
    const h = huddles.find((x) => x.id === myHuddleRef.current);
    huddleClient.current?.leave();
    huddleClient.current = null;
    setMyHuddleId(null);
    playSound("huddle_leave");
    if (h) {
      analytics.track("huddle_ui_left", { category: "huddles", target: "huddle", target_id: h.id, conversation_id: h.conversation_id });
      await api.post(`/conversations/${h.conversation_id}/huddle/leave`).catch(() => {});
    }
  }, [huddles]);
  const toggleMute = useCallback(() => {
    const muted = huddleClient.current?.toggleMute();
    playSound(muted ? "mute" : "unmute");
    analytics.track("huddle_mute_toggled", { category: "huddles", label: muted ? "muted" : "unmuted" });
  }, []);

  useEffect(() => {
    const onHide = () => {
      if (!myHuddleRef.current) return;
      const h = huddles.find((x) => x.id === myHuddleRef.current);
      if (h) fetch(`${api.defaults.baseURL}/conversations/${h.conversation_id}/huddle/leave`, { method: "POST", keepalive: true, headers: { Authorization: `Bearer ${getToken()}` } });
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [huddles]);

  const value = {
    me: user, meId, can, conversations, users, usersByHandle, usersById, msgState, threads, huddles, typing, panel, setPanel, current, currentId,
    connected, outboxCount, retryNow: () => { reconnectNow.current(); flushQueued(); }, modal, setModal, myHuddleId, huddleState, newSince, clearNewSince, totalMentions, dmUnread, prefs, jump, clearJump, jumpToMessage,
    loadConversations, loadMessages, selectConversation, sendMessage, editMessage, deleteMessage, toggleReaction, togglePin, toggleSave,
    forwardMessage, loadThread, openThread, openProfile, closePanel, openConversation, toggleStar, createChannel, openDm, joinChannel,
    leaveChannel, updateConversation, addMembers, saveDraft, sendTyping, joinHuddle, leaveHuddle, toggleMute, markRead,
  };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export const useChat = () => useContext(ChatContext);
